<template>
  <div class="forget-password">
    <a-steps class="steps" :current="currentStep - 1">
      <a-step title="身份认证" />
      <a-step title="重置密码" />
      <a-step title="完成" />
    </a-steps>

    <div class="step-panel">
      <Step1
        v-if="currentStep === 1"
        ref="step1Ref"
        :form="form"
        :rules="step1Rules"
        :state="state"
        :submit-loading="state.submitLoading"
        :captcha-config="captchaConfig"
        :show-captcha-msg="showCaptchaMsg"
        :tip-icon-type="tipIconType"
        :tip-icon-color="tipIconColor"
        @get-captcha="getCaptcha"
        @next="goStep2"
      />

      <Step2
        v-else-if="currentStep === 2"
        ref="step2Ref"
        :form="form"
        :rules="step2Rules"
        :submit-loading="state.submitLoading"
        @prev="goStep1"
        @next="submitReset"
      />

      <Step3 v-else @go-login="changeToLogin" />
    </div>

    <a-row
      v-if="currentStep !== 3"
      type="flex"
      justify="space-between"
      class="footer-links"
    >
      <p><a @click="changeToRegister">没有账号？去注册</a></p>
      <p><a @click="changeToLogin">已有账号，去登录</a></p>
    </a-row>
  </div>
</template>

<script>
import Step1 from "./forgetStep/Step1.vue";
import Step2 from "./forgetStep/Step2.vue";
import Step3 from "./forgetStep/Step3.vue";
import {
  sendForgotPasswordCode,
  verifyForgotPasswordCode,
  resetForgotPassword,
} from "@/api/user/forgotPassword/ForgotPassword.js";
import { toBase64Utf8 } from "@/utils/base64";

const INITIAL_FORM = () => ({
  mobile: "",
  captcha: "",
  password: "",
  confirmPassword: "",
});

export default {
  components: {
    Step1,
    Step2,
    Step3,
  },
  data() {
    return {
      currentStep: 1,
      form: INITIAL_FORM(),
      state: {
        time: 60,
        smsSendBtn: false,
        captchaLoading: false,
        submitLoading: false,
        showCaptchaMsg: false,
        captchaStatus: "success",
      },
      showCaptchaMsg: "短信验证码已发送，可能会有延迟，请耐心等待。",
      captchaTimer: null,
      captchaObj: null,
      resetTicket: "",
      verifiedMobile: "",
      captchaConfig: {
        config: {
          captchaId: "e63c338c75217f3dfaccad44160f8369",
          language: "cn",
          product: "bind",
        },
        handler: null,
      },
    };
  },
  computed: {
    step1Rules() {
      return {
        mobile: [
          {
            required: true,
            message: "请输入手机号",
            trigger: ["blur", "change"],
          },
          {
            pattern: /^1[3-9]\d{9}$/,
            message: "手机号格式不正确",
            trigger: ["blur", "change"],
          },
        ],
        captcha: [
          { required: true, message: "请输入验证码", trigger: "blur" },
        ],
      };
    },
    step2Rules() {
      return {
        password: [
          {
            validator: this.validatePasswordComplexity,
            trigger: "blur",
          },
        ],
        confirmPassword: [
          { required: true, message: "请确认密码", trigger: "blur" },
          {
            validator: this.validateConfirmPassword,
            trigger: "blur",
          },
        ],
      };
    },
    tipIconType() {
      const map = {
        success: "check-circle",
        warning: "exclamation-circle",
        error: "close-circle",
      };
      return map[this.state.captchaStatus] || map.success;
    },
    tipIconColor() {
      const map = {
        success: "#52c41a",
        warning: "#faad14",
        error: "#f5222d",
      };
      return map[this.state.captchaStatus] || map.success;
    },
  },
  created() {
    this.captchaConfig.handler = this.captchaHandler;
  },
  beforeDestroy() {
    this.clearCaptchaTimer();
  },
  watch: {
    "form.mobile"(value) {
      if (this.resetTicket && value !== this.verifiedMobile) {
        this.clearResetTicket();
      }
    },
  },
  methods: {
    goStep2() {
      this.state.submitLoading = true;
      this.clearResetTicket();
      verifyForgotPasswordCode({
        mobile: this.form.mobile,
        captcha: this.form.captcha,
      })
        .then((res) => {
          if (res && res.code === "200" && res.data && res.data.resetTicket) {
            this.resetTicket = res.data.resetTicket;
            this.verifiedMobile = this.form.mobile;
            this.currentStep = 2;
            return;
          }

          this.$notification.error({
            message: "验证失败",
            description: (res && res.msg) || "请重新验证短信验证码",
          });
        })
        .catch(() => {
          this.$notification.error({
            message: "验证失败",
            description: "网络异常，请稍后重试",
          });
        })
        .finally(() => {
          this.state.submitLoading = false;
        });
    },
    goStep1() {
      this.clearResetTicket();
      this.form.password = "";
      this.form.confirmPassword = "";
      this.currentStep = 1;
    },
    changeToLogin() {
      this.resetAll();
      this.$emit("forgetToLogin");
    },
    changeToRegister() {
      this.resetAll();
      this.$emit("forgetToRegister");
    },
    validatePasswordComplexity(rule, value, callback) {
      if (!value) {
        callback("请输入密码");
        return;
      }

      if (value.length < 8 || value.length > 20) {
        callback("密码长度需为 8-20 位");
        return;
      }

      const upper = /[A-Z]/;
      const lower = /[a-z]/;
      const digit = /[0-9]/;
      const special =
        /[~!@#$%^&*()_\-+=<>?{}[\]|:;"',./`！￥…（）—【】、；‘’，。《》？]/;

      if (
        !upper.test(value) ||
        !lower.test(value) ||
        !digit.test(value) ||
        !special.test(value)
      ) {
        callback("密码需包含大写字母、小写字母、数字和特殊符号");
        return;
      }

      if (this.form.mobile && value.includes(this.form.mobile)) {
        callback("密码不能包含手机号");
        return;
      }

      callback();
    },
    validateConfirmPassword(rule, value, callback) {
      if (value && value !== this.form.password) {
        callback("两次输入的密码不一致");
        return;
      }
      callback();
    },
    getCaptcha() {
      if (!/^1[3-9]\d{9}$/.test(this.form.mobile)) {
        this.state.captchaStatus = "warning";
        this.showCaptchaMsg = "请先输入正确的手机号";
        this.state.showCaptchaMsg = true;
        return;
      }

      if (this.captchaConfig.config.product === "bind") {
        if (this.captchaObj) {
          this.captchaObj.showCaptcha();
        } else {
          this.$notification.warning({
            message: "验证码加载中",
            description: "请稍后重试",
          });
        }
        return;
      }

      this.validateCaptchaAndSendSms();
    },
    countDown() {
      this.clearCaptchaTimer();
      this.state.smsSendBtn = true;
      this.state.time = 60;
      this.state.showCaptchaMsg = true;

      this.captchaTimer = setInterval(() => {
        this.state.time -= 1;
        if (this.state.time <= 0) {
          this.clearCaptchaTimer();
          this.state.smsSendBtn = false;
          this.state.showCaptchaMsg = false;
        }
      }, 1000);
    },
    clearCaptchaTimer() {
      if (this.captchaTimer) {
        clearInterval(this.captchaTimer);
        this.captchaTimer = null;
      }
    },
    clearResetTicket() {
      this.resetTicket = "";
      this.verifiedMobile = "";
    },
    validateCaptchaAndSendSms() {
      this.state.captchaLoading = true;
      const result = this.captchaObj && this.captchaObj.getValidate();

      if (!result) {
        this.$message.warning("请先完成图形验证");
        this.state.captchaLoading = false;
        return;
      }

      sendForgotPasswordCode({
        lotNumber: result.lot_number,
        captchaOutput: result.captcha_output,
        passToken: result.pass_token,
        genTime: result.gen_time,
        mobile: this.form.mobile,
      })
        .then((res) => {
          if (res && res.code === "200") {
            this.state.captchaStatus = "success";
            this.showCaptchaMsg = "短信验证码已发送，可能会有延迟，请耐心等待。";
            this.state.showCaptchaMsg = true;
            this.countDown();
          } else {
            this.state.captchaStatus = "warning";
            this.showCaptchaMsg = (res && res.msg) || "验证码发送失败，请重试";
            this.state.showCaptchaMsg = true;
          }
        })
        .catch(() => {
          this.state.captchaStatus = "error";
          this.showCaptchaMsg = "网络异常，请稍后重试";
          this.state.showCaptchaMsg = true;
        })
        .finally(() => {
          this.state.captchaLoading = false;
        });
    },
    captchaHandler(captchaObj) {
      this.captchaObj = captchaObj;
      captchaObj
        .appendTo("#captcha-forget")
        .onSuccess(() => {
          if (this.captchaConfig.config.product === "bind") {
            this.validateCaptchaAndSendSms();
          }
        })
        .onError(() => {
          this.$notification.warning({
            message: "网络连接异常",
            description: "请刷新后重试",
          });
        });
    },
    submitReset() {
      if (!this.resetTicket || this.form.mobile !== this.verifiedMobile) {
        this.$notification.warning({
          message: "请重新验证",
          description: "短信验证已失效，请重新完成身份认证",
        });
        this.goStep1();
        return;
      }

      this.state.submitLoading = true;
      let password;
      let confirmPassword;
      try {
        password = toBase64Utf8(this.form.password);
        confirmPassword = toBase64Utf8(this.form.confirmPassword);
      } catch (error) {
        this.state.submitLoading = false;
        this.$notification.error({
          message: "重置失败",
          description: "密码编码失败，请重新输入",
        });
        return;
      }

      resetForgotPassword({
        password,
        confirmPassword,
        resetTicket: this.resetTicket,
      })
        .then((res) => {
          if (res && res.code === "200") {
            this.clearResetTicket();
            this.currentStep = 3;
            return;
          }

          if (this.isResetTicketInvalid(res)) {
            this.$notification.error({
              message: "验证已失效",
              description: (res && res.msg) || "请重新验证短信验证码",
            });
            this.goStep1();
            return;
          }

          this.$notification.error({
            message: "重置失败",
            description: (res && res.msg) || "请稍后重试",
          });
        })
        .catch(() => {
          this.$notification.error({
            message: "重置失败",
            description: "网络异常，请稍后重试",
          });
        })
        .finally(() => {
          this.state.submitLoading = false;
        });
    },
    isResetTicketInvalid(res) {
      const msg = (res && res.msg) || "";
      return /ticket|票据|凭证|过期|失效|无效|重新验证/.test(msg);
    },
    resetAll() {
      this.clearCaptchaTimer();
      this.clearResetTicket();
      this.currentStep = 1;
      this.form = INITIAL_FORM();
      this.state.time = 60;
      this.state.smsSendBtn = false;
      this.state.captchaLoading = false;
      this.state.submitLoading = false;
      this.state.showCaptchaMsg = false;
      this.state.captchaStatus = "success";
      this.showCaptchaMsg = "短信验证码已发送，可能会有延迟，请耐心等待。";
      this.$nextTick(() => {
        if (this.$refs.step1Ref && this.$refs.step1Ref.resetForm) {
          this.$refs.step1Ref.resetForm();
        }
        if (this.$refs.step2Ref && this.$refs.step2Ref.resetForm) {
          this.$refs.step2Ref.resetForm();
        }
      });
    },
  },
};
</script>

<style scoped>
.forget-password {
  padding: 8px 24px 12px;
}

.steps {
  max-width: 560px;
  margin: 8px auto 0;
}

.step-panel {
  max-width: 460px;
  margin: 40px auto 0;
}

.footer-links {
  max-width: 460px;
  margin: 22px auto 0;
}

.footer-links p {
  margin-bottom: 0;
}
</style>
